import { FC, useCallback, useState } from 'react';
import { Galleria, GalleriaPassThroughOptions } from 'primereact/galleria';
import S from './Gallery.module.scss';
import { ShortArrowIcon } from 'src/redesign/assets/icons';
import c from 'clsx';

const ptOptions: GalleriaPassThroughOptions = {
  thumbnailContainer: {
    style: {
      background: 'white',
      borderRadius: '8px',
    },
  },
  thumbnailItem: {
    className: S.galleryThumbnailImageWrapper,
  },
  thumbnailItems: {
    style: {
      gap: '6px',
    },
  },
};

type GalleryProps = {
  images: string[];
  onNext?: (prevStep: number, currentStep: number) => void;
  onPrev?: (prevStep: number, currentStep: number) => void;
};

export const Gallery: FC<GalleryProps> = ({ images, onPrev, onNext }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const responsiveOptions = [
    {
      breakpoint: '991px',
      numVisible: 4,
    },
    {
      breakpoint: '767px',
      numVisible: 4,
    },
    {
      breakpoint: '575px',
      numVisible: 4,
    },
  ];

  const handleNext = useCallback(() => {
    setActiveSlide((prevState) => {
      if (prevState === images.length - 1) {
        onNext && onNext(prevState, prevState);
        return prevState;
      }

      const nextStep = ++prevState;
      onNext && onNext(prevState, nextStep);
      return nextStep;
    });
  }, [images.length, onNext]);

  const handlePrev = useCallback(() => {
    setActiveSlide((prevState) => {
      if (!prevState) {
        onPrev && onPrev(0, 0);
        return prevState;
      }
      const nextStep = --prevState;
      onPrev && onPrev(prevState, nextStep);
      return nextStep;
    });
  }, [onPrev]);

  const itemTemplate = (item) => {
    return <img src={item} alt={item.alt} style={{ width: '100%', borderRadius: '6px' }} />;
  };
  const thumbnailTemplate = (item) => {
    return (
      <img
        style={{ width: '62px', height: '40px', objectFit: 'cover' }}
        src={item}
        alt={item.alt}
      />
    );
  };
  // If there's only one image, render it directly without carousel
  if (images.length === 1) {
    return (
      <div className={S.galleryWrapper}>
        <div className={S.singleImageContainer}>{itemTemplate(images[0])}</div>
      </div>
    );
  }

  // Otherwise, render the full Galleria component with navigation
  return (
    <div className={S.galleryWrapper}>
      <Galleria
        pt={ptOptions}
        onItemChange={(e) => setActiveSlide(e.index)}
        value={images}
        responsiveOptions={responsiveOptions}
        numVisible={4}
        item={itemTemplate}
        thumbnail={thumbnailTemplate}
        activeIndex={activeSlide}
        showIndicators={false}
        showItemNavigators={false}
        showThumbnailNavigators={false}
        circular
        draggable
      />
      <div className={S.buttons}>
        <button className={S.buttonWrapper} onClick={handlePrev}>
          <div className={S.button}>
            <div className={S.icon}>
              <ShortArrowIcon />
            </div>
          </div>
        </button>
        <button className={S.buttonWrapper} onClick={handleNext}>
          <div className={S.button}>
            <div className={c(S.icon, S.right)}>
              <ShortArrowIcon />
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};
